import { h, render } from 'preact'
import { useCallback, useState } from 'preact/hooks'

import type { ApiClient } from '../api/client'
import { BoardView } from './BoardView'
import { ChangelogList } from './ChangelogList'
import { Fab } from './Fab'
import { Form, type FormValues } from './Form'
import { MineList } from './MineList'
import { Modal } from './Modal'
import { ReportDetailView } from './ReportDetailView'
import { WIDGET_STYLES } from './styles'
import type { StringKey } from './i18n'

export interface MountOptions {
  host: HTMLElement
  strings: Record<StringKey, string>
  showFAB: boolean
  onSubmit: (values: FormValues) => Promise<void>
  /** v0.7: ApiClient for the conversation-loop reader UI. Optional so
   *  the synthetic / auto-error path keeps working with no host changes. */
  api?: ApiClient
  /** v0.7: callback that returns the host's identified user id, or
   *  undefined when no `identify()` has been called yet. The "My
   *  reports" tab + FAB visibility both depend on this. */
  getExternalId?: () => string | undefined
}

export interface MountHandle {
  open(): void
  close(): void
  dispose(): void
  /** Force a re-render — e.g. after the host calls `identify()` so
   *  the FAB becomes visible without a page reload. */
  notifyIdentityChanged(): void
}

type Status = 'idle' | 'submitting' | 'error' | 'success'
type Tab = 'send' | 'mine' | 'changelog' | 'board'
interface State {
  open: boolean
  status: Status
  error?: string
  tab: Tab
  /** When set, MineList is replaced by the detail view for that report id. */
  selectedReportId?: string
}

export function mountWidget(options: MountOptions): MountHandle {
  const shadow = options.host.attachShadow({ mode: 'open' })
  const style = document.createElement('style')
  style.textContent = WIDGET_STYLES
  shadow.appendChild(style)
  const mountPoint = document.createElement('div')
  shadow.appendChild(mountPoint)

  let currentState: State = { open: false, status: 'idle', tab: 'send' }
  let postSubmitTimer: ReturnType<typeof setTimeout> | null = null

  function rerender(state: State) {
    currentState = state
    render(h(Root, { state }), mountPoint)
  }

  /** Strip selectedReportId rather than reassigning `undefined` — under
   *  exactOptionalPropertyTypes the explicit `undefined` is not assignable
   *  back into the optional slot. */
  function clearSelected(s: State): State {
    const { selectedReportId: _drop, ...rest } = s
    void _drop
    return rest
  }

  function Root({ state }: { state: State }) {
    const handleSubmit = useCallback(async (values: FormValues) => {
      rerender({ ...currentState, status: 'submitting' })
      try {
        await options.onSubmit(values)
        rerender({ ...currentState, status: 'success' })
        // Track the close-the-modal timer so dispose() can cancel it.
        // Without this, calling shutdown() mid-submit (e.g. SPA route
        // change right after the user hits send) lets the timer fire
        // after teardown and rerender into a stale shadow root.
        if (postSubmitTimer !== null) clearTimeout(postSubmitTimer)
        postSubmitTimer = setTimeout(() => {
          postSubmitTimer = null
          rerender({
            ...currentState,
            open: false,
            status: 'idle',
            // After a successful submit, jump the user to "My reports" so
            // they immediately see their just-filed report in the list
            // (and the conversation that's about to start).
            tab: options.api ? 'mine' : 'send',
          })
        }, 1200)
      } catch (err) {
        // Don't leak raw API errors to the form's error banner (e.g.
        // `Feedback submit failed: 429 Too Many Requests {…}` straight
        // into a `<div class="error">`). The strings table already has
        // `form.error` — a friendly fallback. Raw error to console.
        if (typeof console !== 'undefined')
          console.warn('[mhosaic] submit:', err)
        rerender({
          ...currentState,
          status: 'error',
          error: options.strings['form.error'],
        })
      }
    }, [])

    const externalId = options.getExternalId?.()
    // FAB-visibility gate: when the host opts into identity (passes a
    // getExternalId callback), require a resolved user before the FAB
    // appears — that's the "only allowed users see the widget" semantics
    // Victor signed off on. Legacy hosts that never wired identity stay
    // on the old "showFAB → visible" path.
    const fabVisible =
      options.showFAB &&
      (options.getExternalId === undefined ? true : Boolean(externalId))
    const showMineTab = Boolean(options.api && externalId)

    return (
      <>
        {fabVisible && (
          <Fab
            label={options.strings['fab.label']}
            onClick={() => rerender({ ...currentState, open: true })}
          />
        )}
        {state.open && (
          <Modal
            onDismiss={() =>
              rerender(clearSelected({ ...currentState, open: false, status: 'idle' }))
            }
            closeLabel={options.strings['form.close']}
            expanded={state.tab === 'board'}
          >
            {showMineTab && (
              <div class="tab-strip" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={state.tab === 'send'}
                  class={`tab-button ${state.tab === 'send' ? 'is-active' : ''}`}
                  onClick={() =>
                    rerender(clearSelected({ ...currentState, tab: 'send' }))
                  }
                >
                  {options.strings['tab.send']}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={state.tab === 'mine'}
                  class={`tab-button ${state.tab === 'mine' ? 'is-active' : ''}`}
                  onClick={() =>
                    rerender(clearSelected({ ...currentState, tab: 'mine' }))
                  }
                >
                  {options.strings['tab.mine']}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={state.tab === 'changelog'}
                  class={`tab-button ${state.tab === 'changelog' ? 'is-active' : ''}`}
                  onClick={() =>
                    rerender(clearSelected({ ...currentState, tab: 'changelog' }))
                  }
                >
                  {options.strings['tab.changelog']}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={state.tab === 'board'}
                  class={`tab-button tab-button--board ${state.tab === 'board' ? 'is-active' : ''}`}
                  onClick={() =>
                    rerender(clearSelected({ ...currentState, tab: 'board' }))
                  }
                >
                  {options.strings['tab.board']}
                </button>
              </div>
            )}
            {state.tab === 'send' && (
              <Form
                strings={options.strings}
                onSubmit={handleSubmit}
                onCancel={() =>
                  rerender({ ...currentState, open: false, status: 'idle' })
                }
                status={state.status}
                {...(state.error !== undefined && { errorMessage: state.error })}
              />
            )}
            {state.tab === 'mine' && options.api && externalId && !state.selectedReportId && (
              <MineList
                api={options.api}
                externalId={externalId}
                strings={options.strings}
                onSelect={(row) =>
                  rerender({ ...currentState, selectedReportId: row.id })
                }
              />
            )}
            {(state.tab === 'mine' || state.tab === 'changelog') &&
              options.api &&
              externalId &&
              state.selectedReportId && (
                <ReportDetailView
                  api={options.api}
                  externalId={externalId}
                  reportId={state.selectedReportId}
                  strings={options.strings}
                  onBack={() =>
                    rerender(clearSelected({ ...currentState }))
                  }
                />
              )}
            {state.tab === 'changelog' &&
              options.api &&
              externalId &&
              !state.selectedReportId && (
                <ChangelogList
                  api={options.api}
                  externalId={externalId}
                  strings={options.strings}
                  onSelect={(row) =>
                    rerender({ ...currentState, selectedReportId: row.id })
                  }
                />
              )}
            {state.tab === 'board' && options.api && externalId && (
              // BoardView owns its own master/detail navigation — no
              // selectedReportId routing through the modal-level state.
              <BoardView
                api={options.api}
                externalId={externalId}
                strings={options.strings}
              />
            )}
          </Modal>
        )}
      </>
    )
  }

  rerender(currentState)

  return {
    open() {
      rerender({ ...currentState, open: true })
    },
    close() {
      rerender({ ...currentState, open: false, status: 'idle' })
    },
    dispose() {
      if (postSubmitTimer !== null) {
        clearTimeout(postSubmitTimer)
        postSubmitTimer = null
      }
      render(null, mountPoint)
      options.host.innerHTML = ''
    },
    notifyIdentityChanged() {
      rerender({ ...currentState })
    },
  }
}
